// SimpleTooltip.jsx
import React, { useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

const SimpleTooltip = ({ children, content, offset = 8 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, placement: "top" });

  const show = () => {
    if (!ref.current) return setVisible(true);
    const r = ref.current.getBoundingClientRect();
    const placement = r.top < 60 ? "bottom" : "top";
    const top = placement === "top" ? r.top - offset : r.bottom + offset;
    const left = r.left + r.width / 2;
    setPos({ top, left, placement });
    setVisible(true);
  };
  const hide = () => setVisible(false);

  // keep position updated while visible (handles scroll/resize)
  useLayoutEffect(() => {
    if (!visible) return;
    const update = () => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const placement = r.top < 60 ? "bottom" : "top";
      const top = placement === "top" ? r.top - offset : r.bottom + offset;
      const left = r.left + r.width / 2;
      setPos({ top, left, placement });
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [visible, offset]);

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={show}
        onMouseLeave={hide}
        style={{ display: "inline-block" }}
      >
        {children}
      </span>

      {visible &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            // fixed => positioned relative to viewport (not affected by parent stacking contexts)
            style={{
              position: "fixed",
              top: `${pos.top}px`,
              left: `${pos.left}px`,
              transform:
                pos.placement === "top"
                  ? "translateX(-50%) translateY(-100%)"
                  : "translateX(-50%) translateY(0)",
              pointerEvents: "none",            // tooltip won't steal mouse events
              background: "rgba(0,0,0,0.9)",
              color: "#fff",
              padding: "6px 10px",
              borderRadius: 4,
              maxWidth: 340,
              zIndex: 2147483647,               // huge z-index to beat everything
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
              fontSize: 13,
            }}
          >
            {typeof content === "string" ? content.replace(/\n/g, " ") : content}
          </div>,
          document.body
        )}
    </>
  );
};

export default SimpleTooltip;
